import { TurnstileServerValidationResponse } from "@marsidev/react-turnstile";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "@remix-run/node";
import { defer, json, useActionData, useLoaderData } from "@remix-run/react";
import { Form } from "~/components/elements/Form";
import { cloudflareTurnstile, ROUTES } from "~/lib/constants";
import { formDataToObject, jsonError, turnstileVerify } from "~/lib/utils";
import { Authenticator } from "~/services/auth.server";

const form = {
  schema: [
    {
      type: "object",
      properties: {
        email: {
          type: "string",
          format: "email",
          minLength: 1,
          errorMessage: {
            format: "Invalid",
            minLength: "Required",
          },
        },
        password: {
          type: "string",
          minLength: 1,
          errorMessage: {
            minLength: "Required",
          },
        },
      },
      required: ["email", "password"],
    },
  ],
  title: "Login to Journi",
  subtitle: {
    text: "Don't have an account?",
    link: {
      to: "/register",
      text: "Sign up",
    },
  },
  footer: {
    text: "Forgot your password?",
    link: {
      to: "/recover",
      text: "Reset Password",
    },
  },
  inputs: [
    [
      {
        label: "Email Address",
        name: "email",
        type: "email",
      },
      {
        label: "Password",
        name: "password",
        type: "password",
      },
    ],
  ],
  submit: {
    label: "Login",
  },
  confirmation: {
    success: "Success",
    error: "Error",
  },
  background: {
    image: {
      src: "https://imagedelivery.net/5u-BnzY4d8R245ADRh1vnA/dfd77b6b-4d2e-47ea-3922-60824a447000/public",
    },
  },
};

export type TFormLogin = typeof form;

export default function Route() {
  const { form, env } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const responseError = actionData?.error?.message;
  const responseTimestamp = Date.now();

  return (
    <Form
      form={form as TFormLogin}
      responseError={responseError}
      responseTimestamp={responseTimestamp}
      turnstile={{ siteKey: env.CF_TURNSTILE_SITE_KEY }}
    />
  );
}

export async function action({ request, context }: ActionFunctionArgs) {
  const { env } = context.cloudflare;
  const authenticator = Authenticator({ context });

  const fields = await formDataToObject(request.clone());

  // Turnstile
  const turnstileVerification = await turnstileVerify({ request, fields, env });

  if (turnstileVerification) {
    return turnstileVerification;
  }

  // Login

  try {
    return await authenticator.authenticate("user-pass", request, {
      successRedirect: ROUTES.dashboard,
      throwOnError: true,
    });
  } catch (err) {
    if (err instanceof Response) {
      throw err;
    }

    return json({
      error: {
        message: (err as Error).message,
      },
    });
  }
}

export async function loader({ request, context }: LoaderFunctionArgs) {
  const { env } = context.cloudflare;
  const authenticator = Authenticator({ context });

  await authenticator.isAuthenticated(request, {
    successRedirect: ROUTES.dashboard,
  });

  return defer({
    form,
    env: {
      CF_TURNSTILE_SITE_KEY: env.CF_TURNSTILE_SITE_KEY,
    },
  });
}
